import {makeAutoObservable} from "mobx";
import { IExchange, IMoneyDto } from "../rma-types";
import { toNumber } from "../../../../models/model-types-methods";
import CustomerCaseStore from "./customer-case-store";

class RmaExchangeStore {
    private _model: IExchange = {} as IExchange;

    constructor(model: IExchange, private _customerCaseStore: CustomerCaseStore) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._setModel(ensureValidModel(model));
    }

    public get name(): string { return this._model.new_item_product_name; }
    public get variantTitle(): string { return this._model.new_item_variant_title; }
    public get image(): string | null { return this._model.new_item_image_url; }
    public get sku(): string | null { return this._model.sku; }
    public get quantity(): number { return this._model.quantity ?? 0; }
    public get variantId(): string | null { return this._model.variant_id ?? null; };
    public get catalogPrice(): number {
		return this._model.new_item_catalog_price_time_of_draft_order_release ||
			this._model.new_item_catalog_price_time_of_draft_order ||
			this._model.new_item_catalog_price_time_of_request;
	}
    public get contextualPrice(): number {
		return this._model.new_item_contextual_price_time_of_draft_order_release ||
			this._model.new_item_contextual_price_time_of_draft_order ||
			this._model.new_item_contextual_price_time_of_request;
	}
    public get adjustedPrice(): number | null { return this._model.new_item_adjusted_price; }
    public get catalogCurrency(): string | null { return this._model.new_item_catalog_price_currency_code; }
    public get contextualCurrency(): string | null { return this._model.new_item_contextual_price_currency_code; }
    public get adjustedPriceCurrency(): string | null { return this._model.new_item_adjusted_price_currency; }
    public get productCaseId(): number | null { return this._model.product_case_id; }
    public get discounts(): string[] | null { return this._model.discounts; }
    public get appliedDiscount(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model._applied_discount); }
    public get bxgyDiscountAdjustment(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model._bxgy_discount_adjustment); }

    private _setModel(model: IExchange) {
        this._model = model;
    }

    public getPriceByCurrency(currencyCode: string): number | undefined {
        if (
			// If adjusted price exists and it's the same currency code as the one we're looking for
			this.adjustedPrice && this.adjustedPriceCurrency === currencyCode
	        // And if the adjusted price is less than the catalog or contextual price (depending on the currency code we're looking for)
	        && (
		        (this.catalogCurrency === currencyCode && this.adjustedPrice < this.catalogPrice) ||
		        (this.contextualCurrency === currencyCode && this.adjustedPrice < this.contextualPrice)
	        )
        ) {
	        // Adjusted price should be smaller than item's catalog price to be considered as discounted and effective price
            return this.adjustedPrice
        }
        if (this.contextualCurrency === currencyCode) {
            return this.contextualPrice;
        }
        if (this.catalogCurrency === currencyCode) {
            return this.catalogPrice;
        }
    }

    public get showPriceBreakdown(): boolean {
        return (
            (this.appliedDiscount?.amount || 0) !== 0 ||
            (this.bxgyDiscountAdjustment?.amount || 0) !== 0
        );
    }

    private _takePaymentCurrencyDataFromMoneyDto(moneyDtos: IMoneyDto[] | null): IMoneyDto | undefined {
		if (!moneyDtos) {
			return undefined;
		}
        const paymentCurrencyCode = this._customerCaseStore.paymentCurrencyCode;
        return (moneyDtos ?? []).find(dto => dto.currency_code === paymentCurrencyCode);
    }
}

function ensureValidModel(model: IExchange) {
    return {
        ...model,
        new_item_catalog_price_time_of_request: toNumber(model.new_item_catalog_price_time_of_request),
    }
}

export { RmaExchangeStore as default };
